import useStore from '@/stores/useStore';
import useBrandStore from '@/stores/useBrandStore';

import { Heading, Text, Button, Link, ImgLazy } from '@/components/blocks';
import {
    Container,
    Overflow,
    Card,
    Timeline,
    Scrolling,
} from '@/components/containers';
import { LFlexSystem, LGridSystem } from '@/components/layouts';

import RawMaterialImg from '@/assets/img/icons/rawmaterial.png';
import ProducedFilmImg from '@/assets/img/icons/producedfilm.png';
import PrintedFilmImg from '@/assets/img/icons/printedfilm.png';
import PackagingImg from '@/assets/img/icons/packaging.png';

import RcycleLogo from '@/assets/svg/logos/rcycle.svg';
import ComexiLogo from '@/assets/svg/logos/comexi.svg';
import TwintagLogo from '@/assets/svg/logos/twintag.svg';
import ExxonmobilLogo from '@/assets/svg/logos/exxonmobil.svg';
import ReifenhauserLogo from '@/assets/svg/logos/reifenhauser.svg';

const rawMaterialShape = (data: any[]) => {
    return data.map((info) => {
        console.log(info);

        return {
            company: 'exxonmobil',
            title: 'Raw Material',
            asset: RawMaterialImg,
            url: info.url,
            properties: [
                {
                    bold: 'Lot number',
                    normal: info.lot.id,
                },
                {
                    bold: 'Producer',
                    normal: 'ExxonMobil',
                },
                {
                    bold: 'Grade',
                    normal: info.grade.id,
                },
                {
                    bold: 'PCR Content (Chemical Recycling)',
                    normal: info.grade.pcrContentChemicalRecycling,
                },
                {
                    bold: 'Product Brand',
                    normal: info.brand.productBrand,
                },
                {
                    bold: 'Melting Temperature',
                    normal: info.grade.meltingTemperature,
                },
                {
                    bold: 'Melt Temperature Min',
                    normal: info.grade.meltTemperatureMin,
                },
                {
                    bold: 'Melt Temperature Max',
                    normal: info.grade.meltTemperatureMax,
                },
            ],
        };
    });
};

const LandingPage = () => {
    const data = useStore((state) => state.data);
    const companyName = useBrandStore((state) => state.companyName);

    const exxonmobil = {
        logo: ExxonmobilLogo,
        url: 'https://www.exxonmobil.be/',
    };

    const reifenhauser = {
        logo: ReifenhauserLogo,
        url: 'https://reifenhauser.com/',
    };

    const comexi = {
        logo: ComexiLogo,
        url: 'https://comexi.com/',
    };

    const rcycle = {
        logo: RcycleLogo,
        url: 'https://r-cycle.org/',
    };

    const twintag = {
        logo: TwintagLogo,
        url: 'https://twintag.com/',
    };

    const staticInfo = {
        rcycle: {
            logo: RcycleLogo,
            partners: [exxonmobil, comexi, twintag, reifenhauser],
            subTitle: ['The Digital Passport', 'for Sustainable Plastics'],
            description:
                'The open tracing standard enables a genuine circular economy and highly efficient process chains.',
            email: 'https://www.r-cycle.org/',
        },
        comexi: {
            logo: ComexiLogo,
            partners: [exxonmobil, rcycle, twintag, reifenhauser],
            subTitle: ['Better packaging', 'for a better world'],
            description:
                'Global suppliers for flexible packaging, developing printers, laminators, slitters, and automated logistic solutions.',
            email: 'https://comexi.com/',
        },
        reifenhauser: {
            logo: ReifenhauserLogo,
            partners: [exxonmobil, rcycle, comexi, twintag],
            subTitle: [
                'Technologies and Know-How',
                'for Success in Plastics Extrusion',
            ],
            description:
                'Offer solutions to meet the sustainability trend and become a preferred supplier for sustainable products that are in global demand.',
            email: 'https://reifenhauser.com/',
        },
    };

    const film = data.timeline.reifenhauser
        ? {
              company: 'reifenhauser',
              title: 'Film',
              asset: ProducedFilmImg,
              url: data.timeline.reifenhauser.url,
              properties: [
                  {
                      bold: 'LGTIN',
                      normal: data.timeline.reifenhauser.LGTIN,
                  },
                  {
                      bold: 'Product Name',
                      normal: data.timeline.reifenhauser.productName,
                  },

                  {
                      bold: 'Thickness',
                      normal: data.timeline.reifenhauser.thickness,
                  },
                  {
                      bold: 'Density',
                      normal: data.timeline.reifenhauser.density,
                  },
                  {
                      bold: 'Ingredient List',
                      list: [
                          {
                              bold: 'PE-LLD',
                              normal: data.timeline.reifenhauser.pelld,
                          },
                          {
                              bold: 'PE-HD',
                              normal: data.timeline.reifenhauser.pehd,
                          },
                          {
                              bold: 'TIO2',
                              normal: data.timeline.reifenhauser.tio2,
                          },
                      ],
                  },
                  {
                      bold: 'Certified circular polymers',
                      normal: data.timeline.reifenhauser
                          .certifiedCircularPolymers,
                  },
                  {
                      bold: 'Length',
                      normal: data.timeline.reifenhauser.length,
                  },
                  {
                      bold: 'Width',
                      normal: data.timeline.reifenhauser.width,
                  },
                  {
                      bold: 'Reel Weight',
                      normal: data.timeline.reifenhauser.reelWeight,
                  },
                  {
                      bold: 'Type of Treatment',
                      normal: data.timeline.reifenhauser.typeOfTreatment,
                  },
                  {
                      bold: 'Treated Side',
                      normal: data.timeline.reifenhauser.treatedSide,
                  },
              ],
          }
        : null;

    const printedFilm = data.timeline.comexi
        ? {
              company: 'comexi',
              title: 'Printed Film',
              asset: PrintedFilmImg,
              url: data.timeline.comexi.url,
              properties: [
                  {
                      bold: 'LGTIN',
                      normal: data.timeline.comexi.LGTIN,
                  },
                  {
                      bold: 'Product Name',
                      normal: data.timeline.comexi.productName,
                  },

                  {
                      bold: 'Print Type',
                      normal: data.timeline.comexi.printType,
                  },
                  {
                      bold: 'Ingredient List',
                      list: [
                          {
                              bold: 'PE-LLD',
                              normal: data.timeline.comexi.pelld,
                          },
                          {
                              bold: 'PE-HD',
                              normal: data.timeline.comexi.pehd,
                          },
                          {
                              bold: 'Ink',
                              normal: data.timeline.comexi.ink,
                          },
                          {
                              bold: 'TIO2',
                              normal: '34.3 %',
                          },
                          {
                              bold: 'Lacquer, water-based',
                              normal: data.timeline.comexi.lacquerWaterBased,
                          },
                      ],
                  },
                  {
                      bold: 'Colour of Basic Material',
                      normal: data.timeline.comexi.colourOfBasicMaterial,
                  },
                  {
                      bold: 'Colour of Printing',
                      normal: data.timeline.comexi.colourOfPrinting,
                  },
                  {
                      bold: 'Length',
                      normal: data.timeline.comexi.length,
                  },
                  {
                      bold: 'Width',
                      normal: data.timeline.comexi.width,
                  },
                  {
                      bold: 'Reel Weight',
                      normal: data.timeline.comexi.reelWeight,
                  },
              ],
          }
        : null;

    const pouch = data.timeline.rcycle
        ? {
              company: 'rcycle',
              title: 'Pouch',
              asset: PackagingImg,
              url: data.timeline.rcycle.url,
              properties: [
                  {
                      bold: 'LGTIN',
                      normal: data.timeline.rcycle.LGTIN,
                  },
                  {
                      bold: 'Quantity',
                      normal: data.timeline.rcycle.quantity,
                  },
                  {
                      bold: 'Object Form',
                      normal: data.timeline.rcycle.objectForm,
                  },
                  {
                      bold: 'Product Type',
                      normal: data.timeline.rcycle.productType,
                  },
                  {
                      bold: 'Packaging Name',
                      normal: data.timeline.rcycle.packagingName,
                  },
                  {
                      bold: 'Packaging Weight',
                      normal: data.timeline.rcycle.packagingWeight,
                  },
                  {
                      bold: 'Density',
                      normal: data.timeline.rcycle.density,
                  },
                  {
                      bold: 'Certified circular polymers',
                      normal: data.timeline.reifenhauser
                          .certifiedCircularPolymers,
                  },
                  {
                      bold: 'Ingredient List',
                      list: [
                          {
                              bold: 'PE-LLD',
                              normal: data.timeline.rcycle.pelld,
                          },
                          {
                              bold: 'PE-HD',
                              normal: data.timeline.rcycle.pehd,
                          },
                          {
                              bold: 'Ink',
                              normal: data.timeline.rcycle.ink,
                          },
                          {
                              bold: 'TIO2',
                              normal: data.timeline.rcycle.tio2,
                          },
                          {
                              bold: 'Lacquer, water-based',
                              normal: data.timeline.rcycle.lacquerWaterBased,
                          },
                      ],
                  },
                  {
                      bold: 'Packaging Components',
                      list: [
                          {
                              bold: 'Component Type',
                              normal: data.timeline.rcycle.componentType,
                          },
                          {
                              bold: 'Weight',
                              normal: data.timeline.rcycle.weight,
                          },
                          {
                              bold: 'Main Ingredient',
                              normal: data.timeline.rcycle.mainIngredient,
                          },
                          {
                              bold: 'Removable',
                              normal: data.timeline.rcycle.removable,
                          },
                      ],
                  },
              ],
          }
        : null;

    const sections: any = [];
    let contact: string = '';

    if (companyName === 'rcycle') {
        sections[0] = pouch;
        sections[1] = printedFilm;
        sections[2] = film;
        sections[3] = rawMaterialShape(data.timeline.exxonmobil);

        contact = 'https://www.r-cycle.org/en/contact/';
    }

    if (companyName === 'comexi') {
        sections[0] = printedFilm;
        sections[1] = film;
        sections[2] = rawMaterialShape(data.timeline.exxonmobil);

        contact = 'https://comexi.com/en/contact';
    }

    if (companyName === 'reifenhauser') {
        sections[0] = film;
        sections[1] = rawMaterialShape(data.timeline.exxonmobil);

        contact = 'https://reifenhauser.com/en/contact/';
    }

    const openNewTab = (url: string) => {
        window.open(url, '_blank');
    };

    return (
        <LGridSystem
            sortChildren={'rows'}
            positioning={{ justifyItems: 'center' }}
            gap={2}
        >
            <Heading
                type={'h1'}
                contentPerLine={[
                    'Simplify traceability from',
                    'resin to packaging',
                ]}
            />

            <Overflow height={25}>
                <Timeline>
                    {sections.map((section: any, idxSection: number) => {
                        if (!section) return null;

                        return (
                            <LGridSystem
                                key={idxSection}
                                section
                                sortChildren={'rows'}
                                positioning={{
                                    justifyItems: 'center',
                                }}
                                gap={1.5}
                            >
                                {Array.isArray(section) ? (
                                    <>
                                        <Heading
                                            type={'h2'}
                                            contentPerLine={['Raw Material']} // Yeah, I know, it's not a good solution
                                        />
                                        {section.map(
                                            (sec: any, idx: number) => {
                                                if (!sec) return null;

                                                return (
                                                    <Card
                                                        key={idx}
                                                        content={
                                                            sec.properties
                                                                ? {
                                                                      idxSection:
                                                                          idxSection,
                                                                      asset: sec.asset,
                                                                      company:
                                                                          sec.company,
                                                                      properties:
                                                                          sec.properties,
                                                                      url: sec.url,
                                                                  }
                                                                : null
                                                        }
                                                    />
                                                );
                                            }
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <Heading
                                            type={'h2'}
                                            contentPerLine={[section.title]}
                                        />
                                        <Card
                                            content={
                                                section.properties
                                                    ? {
                                                          idxSection:
                                                              idxSection,
                                                          asset: section.asset,
                                                          company:
                                                              section.company,
                                                          properties:
                                                              section.properties,
                                                          url: section.url,
                                                      }
                                                    : null
                                            }
                                        />
                                    </>
                                )}
                            </LGridSystem>
                        );
                    })}
                </Timeline>
            </Overflow>

            <Container
                company={companyName}
                href={`${staticInfo[companyName!].email}`}
            >
                <LFlexSystem
                    sortChildren={'columns'}
                    positioning={{ justifyItems: 'center' }}
                    gap={1}
                >
                    <Text
                        boldContent={staticInfo[companyName!].subTitle}
                        normalContent={staticInfo[companyName!].description}
                        fontStyle={{
                            size: 'small',
                        }}
                    />
                    <Button
                        content={'Contact us'}
                        onClick={() => openNewTab(contact)}
                    />
                </LFlexSystem>
            </Container>

            <Scrolling>
                {staticInfo[companyName!].partners.map((company, idx) => (
                    <Link key={idx} href={company.url}>
                        <ImgLazy src={company.logo} />
                    </Link>
                ))}
            </Scrolling>
        </LGridSystem>
    );
};

export default LandingPage;
